import React, { useState } from 'react';
import { Collapse } from '@arco-design/web-react';
import { AttributesPanelWrapper } from './AttributesPanelWrapper';
import { BsChevronDown } from 'react-icons/bs';

interface CollapseWrapperProps {
  children: React.ReactNode;
  defaultActiveKey?: string[];
  forceOpenKeys?: string[];
  style?: React.CSSProperties;
  extra?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  isChevron?: boolean;
}

/**
 * CollapseWrapper - Wraps Collapse with AttributesPanelWrapper and collapse-all functionality
 * Provides a unified collapse interface with a collapse-all button in the header
 */
export function CollapseWrapper({
  children,
  defaultActiveKey = [],
  forceOpenKeys = [],
  style,
  extra,
  iconPosition,
  isChevron,
}: CollapseWrapperProps) {
  // Only the sections the user toggled are tracked; everything else follows
  // defaultActiveKey on every render. That way a section that shows up after
  // mount — Listing Settings, once the block uses listing merge tags — renders
  // expanded instead of being stuck at the key set captured on mount.
  const [toggled, setToggled] = useState<Record<string, boolean>>({});

  const openKeys = new Set(defaultActiveKey);
  Object.entries(toggled).forEach(([key, isOpen]) => {
    if (isOpen) openKeys.add(key);
    else openKeys.delete(key);
  });
  forceOpenKeys.forEach(key => openKeys.add(key));
  const activeKeys = Array.from(openKeys);
  const collapsibleOpenKeys = activeKeys.filter(key => !forceOpenKeys.includes(key));

  const handleCollapseAll = () => {
    // Collapse-all overrides the defaults section by section; pressing it again
    // drops the overrides and falls back to the defaults.
    if (!collapsibleOpenKeys.length) {
      setToggled({});
      return;
    }

    setToggled(prev => {
      const next = { ...prev };
      collapsibleOpenKeys.forEach(key => {
        next[key] = false;
      });
      return next;
    });
  };

  return (
    <AttributesPanelWrapper
      style={style}
      onCollapseAll={handleCollapseAll}
      allCollapsed={activeKeys.length === 0}
      extra={extra}
    >
      <Collapse
        className="attribute-panel-collapse"
        accordion={false}
        activeKey={activeKeys}
        onChange={(key, keys) => {
          const toggledKey = key as string;
          const isOpen = ((keys as string[]) || []).includes(toggledKey);
          setToggled(prev => ({ ...prev, [toggledKey]: isOpen }));
        }}
        expandIconPosition={iconPosition}
        expandIcon={isChevron ? <BsChevronDown size='1rem' /> : undefined}
      >
        {children}
      </Collapse>
    </AttributesPanelWrapper>
  );
}
